// src/components/Dashboard.js
import React, { useRef, useEffect, useState } from 'react';
import MapComponent from './MapComponent';
import Tasks from './Tasks';
import WorkerInterface from './WorkerInterface';
import { calculatePolygonArea } from '../services/mapService';

const Dashboard = ({ data, userRole }) => {
  const { 
    fields = [], 
    tasks: initialTasks = [], 
    weather = { current: {}, forecast: [] }, 
    financialMetrics = { revenue: {}, expenses: {}, profit: {} } 
  } = data || {};
  const mapRef = useRef(null);
  const [totalArea, setTotalArea] = useState(0);
  const [cropTypes, setCropTypes] = useState(0);
  const [contracts, setContracts] = useState(0);
  const [tasks, setTasks] = useState(initialTasks);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Calculate total area, crop types, and contracts
  useEffect(() => {
    if (fields && fields.length > 0) {
      // Calculate total area from all fields (hectares)
      const total = fields.reduce((sum, field) => {
        const area = parseFloat(field.area) || 
                    (field.coordinates ? calculatePolygonArea(field.coordinates) : 0);
        return sum + area;
      }, 0);
      
      // Count unique crop types
      const uniqueCrops = new Set();
      fields.forEach(field => {
        if (field.crop) uniqueCrops.add(field.crop);
      });
      
      // Count active contracts
      const activeContracts = fields.filter(field => field.contract).length;
      
      setTotalArea(total.toFixed(2));
      setCropTypes(uniqueCrops.size);
      setContracts(activeContracts);
    }
  }, [fields]);
  
  // Update task stats
  useEffect(() => {
    // Update the original data source
    data.tasks = tasks;
  }, [tasks, data]);
  
  const handleTasksUpdate = (updatedTasks) => {
    setTasks(updatedTasks);
  };
  
  const renderWeatherIcon = (condition) => {
    if (!condition) return '☀️';
    
    switch(condition.toLowerCase()) {
      case 'sunny': return '☀️';
      case 'partly cloudy': return '⛅';
      case 'cloudy': return '☁️';
      case 'rain': return '🌧️';
      case 'storm': return '⛈️';
      case 'snow': return '❄️';
      default: return '☀️';
    }
  };

  // Calculate task statistics
  const highPriorityCount = tasks.filter(task => task.priority === 'High').length;
  const dueTodayCount = tasks.filter(task => task.dueDate === 'Today').length;
  const inProgressCount = tasks.filter(task => task.status === 'In Progress').length;
  const completedCount = tasks.filter(task => task.status === 'Completed').length;

  return (
    <div className="dashboard-container">
      {/* Dashboard Navigation for Managers */}
      {userRole === 'manager' && (
        <div className="mb-4">
          <div className="nav nav-tabs">
            <button 
              className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Prezentare Generală
            </button>
            <button 
              className={`nav-link ${activeTab === 'worker' ? 'active' : ''}`}
              onClick={() => setActiveTab('worker')}
            >
              👷 Interfață Lucrător
            </button>
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {(activeTab === 'overview' || userRole !== 'manager') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <MapComponent 
              fields={fields}
              showDrawingControls={false} // Hide drawing controls on dashboard
              ref={mapRef}
            />
          </div>
      
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-lg mb-4">Weather</h3>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-gray-500">Current Conditions</div>
            <div className="text-2xl font-bold flex items-center">
              {renderWeatherIcon(weather.current?.condition)}
              <span className="ml-2">{weather.current?.temp || 'N/A'}</span>
            </div>
            <div className="text-sm text-gray-600">
              Humidity: {weather.current?.humidity || 'N/A'} | Wind: {weather.current?.wind || 'N/A'}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm font-medium text-gray-500 mb-2">5-Day Forecast</div>
          <div className="flex justify-between">
            {(weather.forecast || []).slice(0, 5).map((day, index) => (
              <div key={index} className="text-center">
                <div className="text-xs font-medium">{day?.day || `Day ${index + 1}`}</div>
                <span className="text-xl block my-1">{renderWeatherIcon(day?.condition)}</span>
                <div className="text-xs">{day?.high || 'N/A'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-lg mb-3">Field Summary</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Total Area</div>
              <div className="text-xl font-bold">{totalArea} ha</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Crops Planted</div>
              <div className="text-xl font-bold">{cropTypes} types</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Field Health</div>
              <div className="text-xl font-bold">Good</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Contracts</div>
              <div className="text-xl font-bold">{contracts} active</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-lg mb-3">Task Summary</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-red-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">High Priority</div>
              <div className="text-xl font-bold">{highPriorityCount} tasks</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Due Today</div>
              <div className="text-xl font-bold">{dueTodayCount} task</div>
            </div>
            <div className="bg-green-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">In Progress</div>
              <div className="text-xl font-bold">{inProgressCount} task</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Completed</div>
              <div className="text-xl font-bold">{completedCount} tasks</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-lg mb-3">Financial Overview</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Revenue</div>
              <div className="text-xl font-bold">{financialMetrics.revenue?.total || '€0'}</div>
            </div>
            <div className="bg-red-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Expenses</div>
              <div className="text-xl font-bold">{financialMetrics.expenses?.total || '€0'}</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Profit</div>
              <div className="text-xl font-bold">{financialMetrics.profit?.total || '€0'}</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-md">
              <div className="text-sm text-gray-600">Profit/Ha</div>
              <div className="text-xl font-bold">{totalArea > 0 && financialMetrics.profit?.total ? 
                (parseFloat(financialMetrics.profit.total.replace(/[^0-9.-]+/g, '')) / parseFloat(totalArea)).toFixed(2) : 
                '0.00'} €/ha</div>
            </div>
          </div>
        </div>
      </div>
      
          <div className="lg:col-span-3">
            <Tasks data={{ tasks }} onUpdate={handleTasksUpdate} />
          </div>
        </div>
      )}

      {/* Worker Interface Tab */}
      {activeTab === 'worker' && userRole === 'manager' && (
        <div className="worker-interface-section">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h3 className="text-xl font-semibold text-blue-800 mb-2">
              👷 Interfață Lucrător - Vizualizare Manager
            </h3>
            <p className="text-blue-600">
              Aceasta este interfața pe care o văd lucrătorii. Poți monitoriza activitățile și sesiunile de lucru.
            </p>
          </div>
          <WorkerInterface data={{ fields }} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
